import React, { useId, useState } from 'react';
import { Alert, Box, Button, Container, CssBaseline, Stack, TextField, Toolbar, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Card } from '../design-system/components/Card/Card';
import { PageTitle } from '../design-system/components/PageTitle';
import { contentSpacing } from '../design-system/tokens/base/contentStyles';
import { PortalBrand, portalBrandStyles, portalHeaderStyles } from './layout/PortalBrand';

interface ContractActivationProps {
  onLogout: () => void;
  onRegister?: (contractCode: string) => Promise<void>;
}

export function ContractActivation({ onLogout, onRegister }: ContractActivationProps) {
  const inputId = useId();
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const [contractCode, setContractCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!contractCode.trim() || submitting || !onRegister) return;
    setSubmitting(true);
    setError('');
    try {
      await onRegister(contractCode.trim());
    } catch (failure: unknown) {
      const status = (failure as { response?: { status?: number } } | null)?.response?.status;
      setError(status === 400
        ? 'No se ha podido validar el código de contrato con el correo de tu sesión.'
        : 'No se ha podido completar el acceso. Inténtalo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default', color: 'text.primary' }}>
      <CssBaseline />
      <Box component="header">
        <Toolbar sx={portalHeaderStyles}>
          <Box sx={portalBrandStyles}><PortalBrand /></Box>
        </Toolbar>
      </Box>
      <Container component="main" maxWidth="sm" sx={{ my: 'auto', py: { xs: 3, sm: 6 } }}>
        <Card>
          <Stack spacing={contentSpacing.sectionGap}>
            <PageTitle>Activa tu acceso</PageTitle>
            <Typography variant="body2" color="text.secondary">
              Al ser tu primer acceso necesitamos verificar un último dato
            </Typography>
            <Box component="form" onSubmit={submit} autoComplete="off" aria-busy={submitting}>
              <Stack spacing={contentSpacing.sectionGap}>
                <TextField id={inputId} label="Código de contrato" type="text" size="small" value={contractCode}
                  onChange={(event) => setContractCode(event.target.value)}
                  placeholder="Introduce tu código de contrato" autoComplete="off" required fullWidth
                  disabled={submitting} error={Boolean(error)}
                  inputProps={{ 'aria-describedby': error ? `${hintId} ${errorId}` : hintId }} />
                <Typography id={hintId} variant="body2" color="text.secondary">
                  Puedes encontrar este código en tu factura o solicítalo si no lo encuentras
                </Typography>
                {error && <Alert id={errorId} severity="error">{error}</Alert>}
                <Button variant="contained" size="medium" type="submit" endIcon={!submitting && <ArrowForwardIcon />}
                  disabled={submitting || !contractCode.trim() || !onRegister}>
                  {submitting ? 'Validando…' : 'Validar contrato y acceder'}
                </Button>
              </Stack>
            </Box>
          </Stack>
        </Card>
        <Box sx={{ mt: contentSpacing.sectionGap, textAlign: 'center' }}>
          <Button variant="text" onClick={onLogout} disabled={submitting} sx={{ color: 'text.secondary' }}>
            Cerrar sesión
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
